'use client';

import Link from 'next/link';
import { PiArrowLeft, PiArrowRight } from 'react-icons/pi';
import { ButtonShadcn } from '@/components/ui/button-shadcn';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalPosts: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  currentLang?: string; // Made optional since we're not using it currently
  selectedCategory?: string | null; // For category-based pagination
  onPageChange?: (page: number) => void;
  labels: {
    previous: string;
    next: string;
    goToPage: string;
    of: string;
  };
}

export default function Pagination({
  currentPage,
  totalPages,
  hasNextPage,
  hasPreviousPage,
  selectedCategory,
  onPageChange,
  currentLang = 'en',
  labels,
}: PaginationProps) {
  
  const createPageUrl = (page: number) => {
    if (selectedCategory) {
      // For category pages, use static pagination URLs
      const baseUrl = currentLang === 'ru'
        ? `/posts/ru/category/${selectedCategory}`
        : `/posts/category/${selectedCategory}`;

      if (page === 1) {
        return baseUrl;
      } else {
        return `${baseUrl}/page/${page}`;
      }
    } else if (page === 1) {
      // Page 1 uses the main /posts route
      return currentLang === 'ru' ? '/posts/ru' : '/posts';
    } else {
      // Other pages use /posts/page/[number] or /posts/ru/page/[number]
      return currentLang === 'ru'
        ? `/posts/ru/page/${page}`
        : `/posts/page/${page}`;
    }
  };

  const navigateToPage = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    } else {
      window.location.href = createPageUrl(page);
    }
  };

  // Don't render if only one page
  if (totalPages <= 1) return null;

  // Calculate visible page numbers (responsive: fewer on mobile)
  const getVisiblePages = () => {
    // Responsive delta: fewer pages on mobile
    const delta =
      typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    // Remove duplicates and sort
    return [...new Set(rangeWithDots)];
  };

  const visiblePages = getVisiblePages();

  return (
    <div className='w-full flex flex-col items-center gap-4 mt-8 mb-6'>
      <div className='flex items-center justify-center gap-2 sm:gap-4 w-full max-w-2xl mx-auto'>
        {/* Previous button */}
        {hasPreviousPage ? (
          onPageChange ? (
            <ButtonShadcn
              variant='outline'
              size='sm'
              className='flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3'
              onClick={() => navigateToPage(currentPage - 1)}
            >
              <PiArrowLeft size={14} className='sm:size-4' />
              <span className='hidden sm:inline'>{labels.previous}</span>
            </ButtonShadcn>
          ) : (
            <Link href={createPageUrl(currentPage - 1)} className='block'>
              <ButtonShadcn
                variant='outline'
                size='sm'
                className='flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3'
              >
                <PiArrowLeft size={14} className='sm:size-4' />
                <span className='hidden sm:inline'>{labels.previous}</span>
              </ButtonShadcn>
            </Link>
          )
        ) : (
          <ButtonShadcn
            variant='outline'
            size='sm'
            disabled={true}
            className='flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3'
          >
            <PiArrowLeft size={14} className='sm:size-4' />
            <span className='hidden sm:inline'>{labels.previous}</span>
          </ButtonShadcn>
        )}

        {/* Page numbers - SEO optimized with Links */}
        <div className='flex items-center gap-1'>
          {visiblePages.map((page, index) => {
            if (page === '...') {
              return (
                <span
                  key={`dots-${index}`}
                  className='px-2 sm:px-3 py-2 text-zinc-400 text-xs sm:text-sm'
                >
                  ...
                </span>
              );
            }

            const pageNum = page as number;
            const isCurrentPage = pageNum === currentPage;

            return isCurrentPage ? (
              <ButtonShadcn
                key={pageNum}
                variant='default'
                size='sm'
                className='text-xs sm:text-sm min-w-[32px] sm:min-w-[36px] h-8 sm:h-9'
              >
                {pageNum}
              </ButtonShadcn>
            ) : onPageChange ? (
              <ButtonShadcn
                key={pageNum}
                variant='outline'
                size='sm'
                className='text-xs sm:text-sm min-w-[32px] sm:min-w-[36px] h-8 sm:h-9'
                onClick={() => navigateToPage(pageNum)}
              >
                {pageNum}
              </ButtonShadcn>
            ) : (
              <Link
                key={pageNum}
                href={createPageUrl(pageNum)}
                className='block'
              >
                <ButtonShadcn
                  variant='outline'
                  size='sm'
                  className='text-xs sm:text-sm min-w-[32px] sm:min-w-[36px] h-8 sm:h-9'
                >
                  {pageNum}
                </ButtonShadcn>
              </Link>
            );
          })}
        </div>

        {/* Next button */}
        {hasNextPage ? (
          onPageChange ? (
            <ButtonShadcn
              variant='outline'
              size='sm'
              className='flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3'
              onClick={() => navigateToPage(currentPage + 1)}
            >
              <span className='hidden sm:inline'>{labels.next}</span>
              <PiArrowRight size={14} className='sm:size-4' />
            </ButtonShadcn>
          ) : (
            <Link href={createPageUrl(currentPage + 1)} className='block'>
              <ButtonShadcn
                variant='outline'
                size='sm'
                className='flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3'
              >
                <span className='hidden sm:inline'>{labels.next}</span>
                <PiArrowRight size={14} className='sm:size-4' />
              </ButtonShadcn>
            </Link>
          )
        ) : (
          <ButtonShadcn
            variant='outline'
            size='sm'
            disabled={true}
            className='flex items-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-3'
          >
            <span className='hidden sm:inline'>{labels.next}</span>
            <PiArrowRight size={14} className='sm:size-4' />
          </ButtonShadcn>
        )}
      </div>

      {/* Quick jump (for large numbers of pages, hidden on mobile) */}
      {totalPages > 10 && (
        <div className='hidden sm:flex items-center gap-2 text-sm'>
          <span className='text-zinc-600'>{labels.goToPage}</span>
          <input
            type='number'
            min='1'
            max={totalPages}
            placeholder={currentPage.toString()}
            className='w-16 px-2 py-1 text-center border rounded text-sm'
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const target = e.target as HTMLInputElement;
                const page = parseInt(target.value);
                if (page >= 1 && page <= totalPages) {
                  navigateToPage(page);
                }
              }
            }}
          />
          <span className='text-zinc-600'>{labels.of} {totalPages}</span>
        </div>
      )}
    </div>
  );
}
